import { FC, useEffect, useState } from "react";
import { Modal, Form, Input, InputNumber, App, Tabs } from "antd";
import { useTranslation } from "react-i18next";
import { addDailyItem, updateDailyItem } from "@/apis/reimburse";
import { AddDailyRequestItem, RequestDailyItem } from "@/types/reimburse";
import { Product } from "@/types/product";
import SDoc from "@/components/SDoc";
import ProductSelect from "@/components/Selector/ProductSelect";
import { numFormmater } from "@/pages/Requirement/util";

interface AddOrEditDailyItemProps {
  open: boolean;
  applicationId: string;
  editingItem: RequestDailyItem | null;
  onSuccess: (item: RequestDailyItem) => void;
  onClose: () => void;
  productMode?: "CODE" | "NO_CODE";
}

const AddOrEditDailyItem: FC<AddOrEditDailyItemProps> = ({
  open,
  applicationId,
  editingItem,
  onSuccess,
  onClose,
  productMode = "NO_CODE"
}) => {
  const { t } = useTranslation();
  const { notification, modal } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [docId, setDocId] = useState<string>("");
  const [activeTab, setActiveTab] = useState<"info" | "attachment">("info");
  const [selectedProductId, setSelectedProductId] = useState<string>("");
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  useEffect(() => {
    if (open) {
      if (editingItem) {
        form.setFieldsValue({
          productCode: editingItem.productCode,
          productName: editingItem.productName,
          productModel: editingItem.productModel,
          count: editingItem.count,
          desc: editingItem.desc || "",
        });
        setDocId(editingItem.docId || "");
        if (productMode === "CODE") {
          setSelectedProductId(editingItem.productCode || "");
        }
      } else {
        form.resetFields();
        setDocId("");
        setSelectedProductId("");
      }
      setActiveTab("info");
    }
  }, [open, editingItem, form, productMode]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const params: AddDailyRequestItem = {
        productCode: productMode === "CODE" ? selectedProduct?.code : values.productCode,
        productName: values.productName,
        productModel: values.productModel || "",
        count: values.count,
        desc: values.desc || "",
        docId,
      };

      let result: RequestDailyItem;
      if (editingItem) {
        result = await updateDailyItem(editingItem.id, params);
        notification.success({
          placement: 'top',
          message: t("common.tip"),
          description: t("common.updateSuccess"),
        });
      } else {
        result = await addDailyItem(applicationId, params);
        notification.success({
          placement: 'top',
          message: t("common.tip"),
          description: t("common.addSuccess"),
        });
      }

      onSuccess(result);
      handleClose();
    } catch (error: unknown) {
      console.error("Form submit error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setDocId("");
    setSelectedProductId("");
    setSelectedProduct(null);
    onClose();
  };

  const handleSelectProduct = (product: Product | null) => {
    if (product) {
      setSelectedProduct(product)
      form.setFieldsValue({
        productCode: product.code,
        productName: product.name,
        productModel: product.model || "",
      });
    }
  };

  const tabItems = [
    {
      key: "info",
      label: t("common.basicInfo"),
      children: (
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          {productMode === "CODE" ? (
            <Form.Item
              label={t("reimburse.selectProducts")}
              // name="productCode"
              rules={[{ required: true, message: t("reimburse.plSelectProducts") }]}
            >
              <ProductSelect
                value={selectedProductId}
                onChange={setSelectedProductId}
                getSelectPro={handleSelectProduct}
              />
            </Form.Item>
          ) : (
            <Form.Item
              label={t("product.code")}
              name="productCode"
            >
              <Input placeholder={t("product.code")} />
            </Form.Item>
          )}

          <Form.Item
            label={t("product.name")}
            name="productName"
            rules={[{ required: true, message: t("reimburse.plSelectProducts") }]}
          >
            <Input placeholder={t("product.name")} disabled={productMode === "CODE"} />
          </Form.Item>

          <Form.Item
            label={t("product.brand1")}
            name="productModel"
          >
            <Input placeholder={t("product.brand1")} disabled={productMode === "CODE"} />
          </Form.Item>

          <Form.Item
            label={t("reimburse.count")}
            name="count"
            rules={[{ required: true, message: t("reimburse.count") }]}
          >
            <InputNumber changeOnWheel
              formatter={val => numFormmater(Number(val || 0))}
              style={{ width: "100%" }}
              min={1}
              precision={0}
              placeholder={t("reimburse.count")}
            />
          </Form.Item>

          <Form.Item
            label={t("common.desc")}
            name="desc"
          >
            <Input.TextArea
              rows={3}
              placeholder={t("common.desc")}
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      ),
    },
    {
      key: "attachment",
      label: t("common.attachment"),
      children: (
        <SDoc
          docId={docId}
          handleNewDocId={(id) => {
            if (!editingItem) {
              setDocId(id);
            }
          }}
        />
      ),
    },
  ];

  return (
    <Modal
      title={editingItem ? t("common.edit") : t("common.add")}
      open={open}
      onOk={handleSubmit}
      onCancel={handleClose}
      confirmLoading={loading}
      destroyOnClose
      width={700}
    >
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as "info" | "attachment")}
        items={tabItems}
      />
    </Modal>
  );
};

export default AddOrEditDailyItem;
